import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { merge, Observable, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

// If Rollout never reports a configuration (network error, blocked request),
// give up waiting and evaluate with the local defaults — same behavior the app
// had before this guard existed, but without freezing anything.
const MAX_WAIT_MS = 15000;
// The already-fetched probe below is a poll because the fetch event is a plain
// Subject (no replay): a late subscriber would otherwise wait forever.
const POLL_MS = 250;

/**
 * Emits once (and completes) when the Rollout (Rox) configuration is usable.
 *
 * Why: Rox flags read BEFORE `Rox.setup()` finishes its first fetch silently
 * return their local DEFAULT (false) — they do not throw and do not wait. On
 * slow boots the legal entity lands in the store while `settings/u` is still
 * in flight, so anything that evaluates `isFeatureEnabled(...)` on the entity
 * emission latches a false negative (measured on dev2: calendar redirect /
 * unify gate froze whenever the entity beat the toggle fetch; HARs US 12826).
 *
 * Works no matter when the caller subscribes:
 * - before the first fetch → `onConfigurationFetched` fires,
 * - after it → the poll sees `firstNetworkFetchPerformed` already true,
 * - never (offline) → the bounded timer degrades to today's default behavior.
 */
export function whenTogglesReady$(featureToggle: FeatureToggleService): Observable<void> {
  // Private in the .d.ts but a plain runtime property; the public surface
  // offers no way to ask "did the first fetch already happen?".
  const alreadyFetched = () =>
    (featureToggle as unknown as { firstNetworkFetchPerformed?: boolean }).firstNetworkFetchPerformed === true;

  return merge(
    featureToggle.onConfigurationFetched,
    timer(0, POLL_MS).pipe(filter(alreadyFetched)),
    timer(MAX_WAIT_MS),
  ).pipe(
    take(1),
    map(() => undefined),
  );
}
